import { Square } from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  MenuItem,
  Paper,
  Select,
  Typography,
} from '@mui/material';
import { ResponsiveBar } from '@nivo/bar';
import { ResponsiveLine } from '@nivo/line';
import moment from 'moment';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useLoggedInEmployeeId, useLoggedInUserRoles } from '~/components/Auth';
import { SolaForceDataGrid } from '~/components/SolaForceDataGrid';
import {
  AnalyticsReportDefaultProps,
  AnalyticsReportProps,
  AnalyticsReportRoleScope,
  AnalyticsReportScope,
  AnalyticsReportTypes,
  useHeadCountReportData,
} from '~/data/reports';
import { Roles } from '~/data/user';
import { legacyColors } from '~/legacyTheme';
import {
  ReportDatasetLayout,
  ReportDatasetSelector,
  ReportDateRangeSelector,
  ReportDateRangeSelectorMode,
  ReportViewLayout,
  useReportDataset,
} from '~/scenes/PortalModules/Analytics/common';
import {
  UseHeadcountReportOutput,
  useHeadcountReport,
} from '~/scenes/PortalModules/Analytics/People/HeadcountReport/useHeadcrountReport';
import { useSxStyles } from '~/scenes/PortalModules/Analytics/People/HeadcountReport/useStyles';
import { hasOneOfRoles } from '~/services/Permissions';
import { legacyTranslationInterpolation } from '~/utils/helper';
import { mergeSxProps } from '~/utils/helpers/sxProps';

import { ReportTabsLayout } from '../../common/ReportTabsLayout';

enum HeadcountReportTab {
  NumberOfEmployees = 'numberOfEmployees',
  JoinersAndLeavers = 'joinersAndLeavers',
  Table = 'table',
}

type TabLabels = { [key in HeadcountReportTab]: string };

const SelectedYearHeading = (props: {
  reportTab: HeadcountReportTab;
  selectedYear: string;
}) => {
  const { reportTab, selectedYear } = props;

  const sxStyles = useSxStyles();
  const { t } = useTranslation();

  const resolveHeading = () => {
    const startOfYear = moment(`${selectedYear}-01-01`);
    const endOfYear = startOfYear.clone().endOf('year');

    const viewMonthRange = startOfYear.isValid()
      ? `${startOfYear.format('L')} - ${endOfYear.format('L')}`
      : '';

    switch (reportTab) {
      case HeadcountReportTab.NumberOfEmployees:
        return t('label_emp_count_in_month', {
          year: viewMonthRange,
          interpolation: legacyTranslationInterpolation,
        });
      case HeadcountReportTab.JoinersAndLeavers:
        return t('label_emp_joiners_and_leavers_in_month', {
          year: viewMonthRange,
          interpolation: legacyTranslationInterpolation,
        });
      case HeadcountReportTab.Table:
        return viewMonthRange;
      default:
        return '';
    }
  };

  return (
    <Typography variant={'subtitle1'} sx={sxStyles.tabHeading}>
      {resolveHeading()}
    </Typography>
  );
};

const ReportContentTab = (
  props: {
    reportTab: HeadcountReportTab;
    tabLabels: TabLabels;
  } & Pick<UseHeadcountReportOutput, 'charts' | 'table'>,
) => {
  const { reportTab, tabLabels, charts, table } = props;

  const sxStyles = useSxStyles();
  const { t } = useTranslation();

  switch (reportTab) {
    case HeadcountReportTab.Table:
      return (
        <SolaForceDataGrid
          columns={table.columnConfigs}
          rows={table.rows}
          getRowId={(row) => row.rowName}
          hideFooter={true}
          sx={sxStyles.table}
        />
      );
    case HeadcountReportTab.NumberOfEmployees:
      return (
        <>
          <Box sx={sxStyles.chartContainer}>
            <ResponsiveLine
              {...charts.numberOfEmployees}
              margin={{
                top: 20,
                right: 150,
                bottom: 70,
                left: 70,
              }}
              axisBottom={{
                legend: t('laObservationMonths'),
                legendOffset: 50,
                legendPosition: 'middle',
              }}
              axisLeft={{
                legend: tabLabels[HeadcountReportTab.NumberOfEmployees],
                legendOffset: -50,
                legendPosition: 'middle',
                ...charts.numberOfEmployees.axisLeft,
              }}
              useMesh={true}
              enablePointLabel={true}
              tooltip={({ point }) => {
                return (
                  <Paper sx={sxStyles.lineTooltipPaper}>
                    <Typography variant={'subtitle2'}>
                      {point.data.xFormatted}
                    </Typography>
                    <Box sx={sxStyles.lineTooltipPoint}>
                      <Square
                        sx={mergeSxProps(sxStyles.lineTooltipPointColor, {
                          color: point.seriesColor,
                          display: 'block',
                        })}
                      />
                      <Typography
                        variant={'body2'}
                        sx={{ display: 'block' }}
                      >{`${t('laNumberOfEmp')}:`}</Typography>
                      <Typography variant={'subtitle2'}>
                        {point.data.yFormatted}
                      </Typography>
                    </Box>
                  </Paper>
                );
              }}
            />
          </Box>
        </>
      );
    case HeadcountReportTab.JoinersAndLeavers:
      return (
        <Box sx={sxStyles.chartContainer}>
          <ResponsiveBar
            {...charts.joinersAndLeavers}
            keys={['joiners', 'leavers', 'netChange']}
            indexBy={'month'}
            margin={{
              top: 20,
              right: 150,
              bottom: 70,
              left: 70,
            }}
            groupMode={'grouped'}
            layout={'vertical'}
            axisBottom={{
              legend: t('laObservationMonths'),
              legendOffset: 50,
              legendPosition: 'middle',
            }}
            axisLeft={{
              legend: tabLabels[HeadcountReportTab.JoinersAndLeavers],
              legendOffset: -50,
              legendPosition: 'middle',
            }}
            label={(d) => `${d.value === 0 ? '' : d.value}`}
            colors={(d) => {
              switch (d.id) {
                case 'joiners':
                  return 'green';
                case 'leavers':
                  return 'red';
                case 'netChange':
                  return legacyColors.blue;
                default:
                  return '#000000';
              }
            }}
            labelTextColor={'white'}
          />
        </Box>
      );
    default:
      return <></>;
  }
};

export const HeadcountReport = () => {
  const sxStyles = useSxStyles();

  const { t } = useTranslation();
  const loggedInEmployeeId = useLoggedInEmployeeId();
  const myRoles = useLoggedInUserRoles();
  const userHasOneOfRoles = useCallback(
    (roles: Roles[]) => {
      return hasOneOfRoles(myRoles, roles);
    },
    [myRoles],
  );

  const [reportTab, setReportTab] = useState<HeadcountReportTab>(
    HeadcountReportTab.NumberOfEmployees,
  );
  const tabLabels = useMemo((): TabLabels => {
    return {
      [HeadcountReportTab.NumberOfEmployees]: t('laNumberOfEmp'),
      [HeadcountReportTab.JoinersAndLeavers]: t('laJoinersAndLeavers'),
      [HeadcountReportTab.Table]: t('laTable'),
    };
  }, [t]);

  const defaultReportSettings: AnalyticsReportDefaultProps<object> = {
    reportType: {
      type: AnalyticsReportTypes.Team,
      role: AnalyticsReportRoleScope.Manager,
      employeeId: loggedInEmployeeId,
      scope: AnalyticsReportScope.Direct,
    },
    params: {
      startDate: moment().startOf('year').subtract(2, 'years').toDate(),
      endDate: moment().endOf('year').toDate(),
    },
  };

  const reportDatasetConfig = useReportDataset({
    selection: defaultReportSettings,
    config: {
      options: {
        types: [
          ...(userHasOneOfRoles([Roles.HR_ADMIN])
            ? [AnalyticsReportTypes.Company, AnalyticsReportTypes.LegalEntity]
            : []),
          AnalyticsReportTypes.Team,
          AnalyticsReportTypes.HR,
          AnalyticsReportTypes.Unit,
        ],
        roles: [
          AnalyticsReportRoleScope.Manager,
          AnalyticsReportRoleScope.AltManager,
        ],
      },
    },
  });
  const { reportSelection, reportEnabled } = reportDatasetConfig;

  const loadDataSettings = useMemo((): AnalyticsReportProps<object> => {
    const startDate = reportSelection.params?.startDate;
    const endDate = reportSelection.params?.endDate;

    return {
      ...reportSelection,
      params: {
        ...reportSelection.params,
        startDate: !!startDate
          ? moment(reportSelection.params?.startDate)
              .subtract(1, 'year')
              .endOf('year')
              .toDate()
          : undefined,
        endDate,
      },
      reportDisabled: !reportEnabled,
    };
  }, [reportSelection, reportEnabled]);

  const { data, isLoading, failedToLoad } =
    useHeadCountReportData(loadDataSettings);

  const { table, yearSelector, charts } = useHeadcountReport({ data });

  return (
    <Box>
      <ReportViewLayout
        heading={t('laHeadCountReport')}
        slots={{
          toolbar: {
            main: (
              <ReportDatasetLayout showHeading={true}>
                <ReportDatasetSelector
                  {...reportDatasetConfig}
                  isLoading={isLoading}
                  failedToLoad={failedToLoad}
                  renderCurrentParams={({ params }) => {
                    const startDate = moment(params?.startDate);
                    const endDate = moment(params?.endDate);

                    const years = endDate.diff(startDate, 'years') + 1;
                    const startYear = startDate.format('YYYY');
                    const endYear = endDate.format('YYYY');
                    const range =
                      startYear === endYear
                        ? startYear
                        : `${startYear} - ${endYear}`;

                    return <>{`${range} (${years} ${t('laYears')})`}</>;
                  }}
                  renderParamsFields={({ params, onChange }) => {
                    return (
                      <>
                        <ReportDateRangeSelector
                          startDate={params?.startDate}
                          endDate={params?.endDate}
                          onRangeChange={(startDate, endDate) =>
                            onChange({ ...params, startDate, endDate })
                          }
                          minDate={moment()
                            .startOf('year')
                            .subtract(9, 'years')
                            .toDate()}
                          maxDate={moment().endOf('year').toDate()}
                          mode={ReportDateRangeSelectorMode.YearRange}
                        />
                      </>
                    );
                  }}
                />
              </ReportDatasetLayout>
            ),
          },
        }}
      />

      {reportEnabled ? (
        <ReportTabsLayout<HeadcountReportTab>
          reportTab={reportTab}
          setReportTab={setReportTab}
          tabLabels={tabLabels}
        >
          <SelectedYearHeading
            reportTab={reportTab}
            selectedYear={yearSelector.value}
          />
          <ReportContentTab
            reportTab={reportTab}
            tabLabels={tabLabels}
            charts={charts}
            table={table}
          />
          <Box sx={sxStyles.yearNavigation}>
            <Button
              onClick={() => yearSelector.onNavigateYear('previous')}
              disabled={
                yearSelector.isSelectedYear.first ||
                !yearSelector.options.length
              }
            >
              {t('laPrev')}
            </Button>
            <Select
              value={yearSelector.value}
              onChange={(event) =>
                yearSelector.onChangeYear(event.target.value)
              }
            >
              {yearSelector.options.map((option) => {
                return (
                  <MenuItem key={option} value={option}>
                    {option}
                  </MenuItem>
                );
              })}
            </Select>
            <Button
              onClick={() => yearSelector.onNavigateYear('next')}
              disabled={
                yearSelector.isSelectedYear.last || !yearSelector.options.length
              }
            >
              {t('laNext')}
            </Button>
          </Box>
        </ReportTabsLayout>
      ) : (
        <>
          <Alert severity={'error'}>{t('laGeneralError')}</Alert>
        </>
      )}
    </Box>
  );
};
